// Field identity and merging — rules R1, R2 and R7 of the form-building rules
// (docs/plans/custom-field-builder.md; the rules themselves are documented in
// the header of this module because they are the thing most likely to be
// re-litigated later).
//
// WHY THIS EXISTS. Real customer forms are one document per REQUEST TYPE SET:
// a single file with a section per request type ("MEETINGS", "SCHOOL EVENT",
// "FIELD TRIP REQUESTS", …), each repeating the same questions. Extraction
// faithfully reports every occurrence, so a 7-section form yields seven
// "Estimated Attendance" candidates. Creating seven same-named custom fields
// is wrong in FMX for a concrete, verified reason:
//
//   Visibility/value formulas bind their {placeholders} BY FIELD NAME —
//   CustomField.PlaceholderDisplayKey => Name (Fmx.Model/CustomField.cs:226).
//   The comment at CustomField.cs:432-434 spells out that when two fields
//   share a name, placeholder ORDERING decides which one a formula resolves
//   to. Duplicate names therefore make every formula referencing them
//   silently ambiguous.
//
// So: R1 — one field per distinct question per fieldset, identity being the
// normalized name. R2 — the repetition becomes SCOPE (requestTypeIDs = the
// union of the request types whose sections asked it), never extra fields.
// R7 — when the same question carries DIFFERENT OPTIONS per section (the
// document's "Intended Audience" is Faculty/Staff·Students·Families
// internally but Adults·Students·General Public for external groups),
// merging silently would show the wrong choices to the wrong requesters, so
// it is raised for a human decision instead of guessed.
//
// FMX itself does not reject duplicate names (the only such check in
// Fmx.Services/CustomFieldService.cs is scoped to utility fields per
// provider) — this is a modeling rule, enforced here, not an API constraint.
//
// Pure module: no React, no Supabase, no fetch — node-testable.

import { normalizeFieldName } from './dedupe.js';
import { normalizeFieldset } from './fieldsets.js';
import { normalizeFieldType } from './fmxCustomFieldsApi.js';

const CONFIDENCE_RANK = { low: 0, medium: 1, high: 2 };

// Section title for every stage-1 item id, so a merged field can say which
// request-type sections asked it.
export function sectionTitlesBySourceItem(stage1) {
  const map = new Map();
  for (const sec of stage1?.sections || []) {
    for (const item of sec.items || []) map.set(item.id, sec.title || null);
  }
  return map;
}

// The signature that decides whether two occurrences of the same question are
// really the same field: its permanent choices. Option ORDER is deliberately
// ignored — the same choices listed differently across sections is the same
// question, not a conflict — but the option SET and the field type are not.
function compatibilitySignature(f) {
  const options = [...(f.options || [])]
    .map(o => String(o).trim().toLowerCase())
    .sort();
  return JSON.stringify([normalizeFieldType(f.fieldType), options]);
}

// The lowest confidence in the group — merging never manufactures certainty.
function lowestConfidence(group) {
  return group
    .map(f => f.confidence || 'low')
    .reduce((lo, c) => (CONFIDENCE_RANK[c] < CONFIDENCE_RANK[lo] ? c : lo), 'high');
}

/**
 * Group candidate fields by (fieldset, normalized name) and merge each group
 * into a single field.
 *
 * @param fields  validated stage-2 fields (immutable input — never mutated)
 * @param stage1  the structural read, for section titles
 * @returns {{ fields: object[], conflicts: object[] }}
 *   fields    — one entry per distinct question, in first-occurrence order,
 *               each carrying `mergedFrom` (all source item ids, primary
 *               first) and `mergedSections` (deduped section titles)
 *   conflicts — groups that must NOT be silently merged:
 *               { kind: 'options' | 'fieldType', name, fieldset,
 *                 sourceItemIds, sections, detail }
 */
export function mergeCandidates(fields, stage1) {
  const sectionOf = sectionTitlesBySourceItem(stage1);
  const groups = new Map(); // key → [field…] in document order
  const order = [];

  for (const f of fields || []) {
    const fieldset = normalizeFieldset(f.fieldset) || f.fieldset;
    const key = `${fieldset}::${normalizeFieldName(f.name)}`;
    if (!groups.has(key)) { groups.set(key, []); order.push(key); }
    groups.get(key).push(f);
  }

  const out = [];
  const conflicts = [];

  for (const key of order) {
    const group = groups.get(key);
    const sectionsOf = (list) => [...new Set(
      list.map(f => sectionOf.get(f.sourceItemId)).filter(Boolean)
    )];

    if (group.length === 1) {
      const [only] = group;
      out.push({ ...only, mergedFrom: [only.sourceItemId], mergedSections: sectionsOf(group) });
      continue;
    }

    // Occurrences only merge when they agree on the permanent choices, so the
    // group is partitioned by compatibility first. The common real case is a
    // question asked identically in five sections and DIFFERENTLY in a sixth
    // (the sample document's "Intended Audience" offers an external audience
    // list): the five still merge, and only the odd one out is raised. Merging
    // all of them would silently destroy one of the option lists (R7).
    const subgroups = new Map();
    for (const f of group) {
      const sig = compatibilitySignature(f);
      if (!subgroups.has(sig)) subgroups.set(sig, []);
      subgroups.get(sig).push(f);
    }

    // Repetition ACROSS sections is request-type repetition and merges.
    // Repetition WITHIN one section is a different animal: a checklist with two
    // "Notes" boxes in the same section really does need two fields, and they
    // need distinct names — merging would silently delete one. Those keep their
    // duplicate-name error, which asks for a rename.
    for (const [sig, sub] of [...subgroups.entries()]) {
      const sections = sub.map(f => sectionOf.get(f.sourceItemId) ?? null);
      if (new Set(sections).size === sections.length) continue; // all distinct
      subgroups.delete(sig);
      const bySection = new Map();
      for (const f of sub) {
        const key = sectionOf.get(f.sourceItemId) ?? ' none';
        if (!bySection.has(key)) bySection.set(key, []);
        bySection.get(key).push(f);
      }
      // One field per occurrence inside a repeating section — nothing merges.
      let n = 0;
      for (const sameSection of bySection.values()) {
        for (const f of sameSection) subgroups.set(`${sig}#same-section-${n++}`, [f]);
      }
      conflicts.push({
        kind: 'sameSection',
        name: sub[0].name,
        fieldset: normalizeFieldset(sub[0].fieldset) || sub[0].fieldset,
        sourceItemIds: sub.map(f => f.sourceItemId),
        sections: [...new Set(sections.filter(Boolean))],
        detail: `“${sub[0].name}” is asked more than once in the same section, so these are separate questions that happen to share a label. Give them distinct names (FMX can’t tell two same-named fields apart, and formulas reference fields by name).`,
        variants: sub.map(f => ({
          sourceItemIds: [f.sourceItemId],
          sections: [sectionOf.get(f.sourceItemId) || null].filter(Boolean),
          fieldType: normalizeFieldType(f.fieldType),
          options: [...(f.options || [])],
        })),
      });
    }

    if (subgroups.size > 1) {
      const [primary] = group;
      const variants = [...subgroups.values()].map(sub => ({
        sourceItemIds: sub.map(f => f.sourceItemId),
        sections: sectionsOf(sub),
        fieldType: normalizeFieldType(sub[0].fieldType),
        options: [...(sub[0].options || [])],
      }));
      const typesDiffer = new Set(variants.map(v => v.fieldType)).size > 1;
      conflicts.push({
        kind: typesDiffer ? 'fieldType' : 'options',
        name: primary.name,
        fieldset: normalizeFieldset(primary.fieldset) || primary.fieldset,
        sourceItemIds: group.map(f => f.sourceItemId),
        sections: sectionsOf(group),
        detail: typesDiffer
          ? `“${primary.name}” is asked ${group.length} times with different field types (${variants.map(v => v.fieldType).join(' vs ')}). Pick one type, then merge them.`
          : `“${primary.name}” is asked ${group.length} times with ${subgroups.size} different option lists (${variants.map(v => `${v.sections.join('/') || 'unknown section'}: ${v.options.join(', ')}`).join(' — ')}). Decide whether it is one field with a combined list, or genuinely different questions per request type.`,
        variants,
      });
    }

    // Merge each compatible subgroup into one field, keeping the first
    // occurrence's permanent choices and the most informative of the rest.
    for (const sub of subgroups.values()) {
      const [primary] = sub;
      out.push({
        ...primary,
        isRequired: sub.some(f => f.isRequired === true),
        allowOtherOption: sub.some(f => f.allowOtherOption === true),
        allowMultipleSelections: sub.some(f => f.allowMultipleSelections === true),
        allowMultipleLines: sub.some(f => f.allowMultipleLines === true),
        disallowNegativeValues: sub.some(f => f.disallowNegativeValues === true),
        description: sub.map(f => f.description).find(d => d) ?? null,
        confidence: lowestConfidence(sub),
        rationale: primary.rationale,
        mergedFrom: sub.map(f => f.sourceItemId),
        mergedSections: sectionsOf(sub),
      });
    }
  }

  return { fields: out, conflicts };
}

/**
 * Resolve a field's request-type scope from the sections it came from (R2).
 *
 * @param mergedSections  the field's `mergedSections`
 * @param sectionRequestTypes { [sectionTitle]: {id,name} | null } — the scope
 *        screen's confirmed section → live request type mapping
 * @returns [{id,name}…] deduped by id, or [] when nothing maps (the field
 *          then falls back to the fieldset-level scope, as before)
 */
export function requestTypesForSections(mergedSections, sectionRequestTypes = {}) {
  const byId = new Map();
  for (const title of mergedSections || []) {
    const rt = sectionRequestTypes[title];
    if (rt && rt.id != null && !byId.has(String(rt.id))) byId.set(String(rt.id), rt);
  }
  return [...byId.values()];
}

/**
 * Apply the confirmed section → request-type mapping to every draft field,
 * setting `scopeOverride.requestTypeIDs` where the field's sections resolve.
 * Fields whose sections don't map are left alone (fieldset scope applies).
 * Returns a new array; inputs are not mutated.
 */
export function applySectionScoping(fields, sectionRequestTypes = {}) {
  return (fields || []).map(f => {
    const requestTypeIDs = requestTypesForSections(f.mergedSections, sectionRequestTypes);
    if (!requestTypeIDs.length) return { ...f };
    return { ...f, scopeOverride: { ...(f.scopeOverride || {}), requestTypeIDs } };
  });
}

// Which draft field a stage-1 item ended up in, following merges — powers
// visibility-rule resolution ("the question that triggers this one") and
// jump-to-source on a merged field.
export function fieldBySourceItem(fields) {
  const map = new Map();
  for (const f of fields || []) {
    for (const id of f.mergedFrom?.length ? f.mergedFrom : [f.sourceItemId]) {
      if (id && !map.has(id)) map.set(id, f);
    }
  }
  return map;
}
